import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

/**
 * Inline animated dot sphere. Exposes setSpeaking/setVolume via ref so the parent
 * (HomePage) can drive its pulse from Vapi events.
 */
const AvaSphere = forwardRef(function AvaSphere({ size }, ref) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const stateRef = useRef({ speaking: false, volume: 0, intensity: 0 });
  const rafRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setSpeaking: (v) => { stateRef.current.speaking = !!v; if (!v) stateRef.current.volume = 0; },
    setVolume: (v) => { if (stateRef.current.speaking) stateRef.current.volume = v; },
    reset: () => { stateRef.current.speaking = false; stateRef.current.volume = 0; },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    // Use parent size if not explicitly given
    const measure = () => {
      const rect = wrapper.parentElement?.getBoundingClientRect() || { width: size || 420, height: size || 420 };
      return Math.min(rect.width || 420, rect.height || 420) || (size || 420);
    };
    let SIZE = size || measure();
    const dpr = window.devicePixelRatio || 1;

    const setup = () => {
      canvas.width = SIZE * dpr;
      canvas.height = SIZE * dpr;
      canvas.style.width = SIZE + 'px';
      canvas.style.height = SIZE + 'px';
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      return ctx;
    };
    let ctx = setup();
    const onResize = () => {
      if (size) return;
      const next = measure();
      if (Math.abs(next - SIZE) > 5) { SIZE = next; ctx = setup(); }
    };
    window.addEventListener('resize', onResize);

    const LAT = 36, LON = 60, dots = [];
    for (let i = 0; i <= LAT; i++) {
      const phi = (i / LAT) * Math.PI;
      const ring = Math.max(1, Math.round(LON * Math.sin(phi)));
      for (let j = 0; j < ring; j++) {
        const theta = (j / ring) * Math.PI * 2;
        dots.push({
          nx: Math.sin(phi) * Math.cos(theta),
          ny: Math.cos(phi),
          nz: Math.sin(phi) * Math.sin(theta),
        });
      }
    }

    const getDisp = (nx, ny, nz, t, I) => {
      const a = Math.atan2(nz, nx);
      const base = Math.sin(a * 2 + t) * 0.5
        + Math.cos(a + t * 0.7) * ny * 0.3
        + Math.sin(a * 4 - t * 1.3) * 0.08
        + Math.cos(ny * 4 + t) * 0.06;
      const pulse = I > 0
        ? Math.sin(a * 3 + t * 3.5) * I * 0.6 + Math.cos(ny * 5 + t * 4) * I * 0.35
        : 0;
      return base + pulse;
    };

    let time = 0, rotY = 0;
    const draw = () => {
      const cx = SIZE / 2, cy = SIZE / 2, R = SIZE * 0.34;
      ctx.clearRect(0, 0, SIZE, SIZE);
      const s = stateRef.current;
      if (s.speaking) s.intensity += (s.volume - s.intensity) * 0.12;
      else s.intensity = Math.max(0, s.intensity - 0.015);
      const I = s.intensity;

      const gA = 0.18 + I * 0.22;
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.75);
      grd.addColorStop(0, `rgba(0,200,230,${gA.toFixed(2)})`);
      grd.addColorStop(0.5, `rgba(0,100,200,${(gA * 0.5).toFixed(2)})`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(cx, cy, R * 0.75, 0, Math.PI * 2); ctx.fill();

      const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
      const projected = dots.map(d => {
        const rx = d.nx * cosY + d.nz * sinY;
        const ry = d.ny;
        const rz = -d.nx * sinY + d.nz * cosY;
        const disp = getDisp(d.nx, d.ny, d.nz, time, I);
        const rFinal = R * (1 + disp * 0.38);
        const depth = (rz + 1) / 2;
        const isRim = disp > 0.25;
        const dotR = isRim ? 2.2 : 1.4;
        const tY = (ry + 1) / 2;
        let cr, cg, cb;
        if (tY > 0.45) {
          const t = (tY - 0.45) / 0.55;
          cr = Math.round(30 + t * 20); cg = Math.round(150 + t * 100); cb = 255;
        } else {
          const t = tY / 0.45;
          cr = Math.round(180 - t * 60); cg = Math.round(30 + t * 50); cb = Math.round(220 + t * 35);
        }
        const bright = isRim ? 1.0 : 0.4 + depth * 0.4;
        const alpha = isRim ? 0.7 + depth * 0.3 : depth * 0.25;
        return {
          sx: cx + rx * rFinal, sy: cy - ry * rFinal, dotR, depth,
          r: Math.min(255, Math.round(cr * bright)),
          g: Math.min(255, Math.round(cg * bright)),
          b: Math.min(255, Math.round(cb * bright)),
          alpha: Math.max(0, Math.min(1, alpha)),
        };
      });
      projected.sort((a, b) => a.depth - b.depth);
      for (const p of projected) {
        if (p.alpha < 0.02) continue;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, p.dotR, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${p.alpha.toFixed(2)})`;
        ctx.fill();
      }
      time += 0.008 + I * 0.014;
      rotY += 0.004 + I * 0.007;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [size]);

  return <div ref={wrapperRef}><canvas ref={canvasRef} className="block pointer-events-none" /></div>;
});

export default AvaSphere;
